import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import type { components } from "../api/schema";

// Type aliases for convenience
type Page = components["schemas"]["Page"];
type PageVersion = components["schemas"]["PageVersion"];
type TiptapDocument = components["schemas"]["TiptapDocument"];
type CreatePageRequest = components["schemas"]["CreatePageRequest"];
type UpdatePageRequest = components["schemas"]["UpdatePageRequest"];

interface PageResponse {
  page: Page;
}

export function usePageVersions(assetUid: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["pages", "asset", assetUid, "versions"],
    queryFn: async (): Promise<{ versions: PageVersion[] }> => {
      const { data, error } = await apiClient.GET(
        "/api/v1/organizations/{orgCode}/assets/{assetUid}/page/versions",
        {
          params: {
            path: {
              orgCode: "demo", // TODO: Get from context
              assetUid,
            },
          },
        }
      );
      if (error) {
        throw new Error((error as any)?.error || "Failed to fetch page versions");
      }
      return (data as unknown as { versions: PageVersion[] }) ?? { versions: [] };
    },
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled,
  });
}

export function usePageByEntity(entityType: string, entityUid: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["pages", entityType, entityUid],
    queryFn: async (): Promise<PageResponse | null> => {
      // Use asset-specific API for assets, generic API for other entity types
      if (entityType === "asset") {
        const { data, error, response } = await apiClient.GET(
          "/api/v1/organizations/{orgCode}/assets/{assetUid}/page",
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                assetUid: entityUid,
              },
            },
          }
        );

        // Handle 404 as a successful "no page found" case
        if (response.status === 404) {
          return null; // Page doesn't exist
        }

        if (error) {
          throw new Error((error as any)?.error || "Failed to fetch asset page");
        }

        return data as unknown as PageResponse;
      } else {
        const { data, error, response } = await apiClient.GET(
          "/api/v1/organizations/{orgCode}/pages/{entityIdentifier}" as any,
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                entityIdentifier: `${entityType}:${entityUid}`,
              },
            },
          }
        );

        // Handle 404 as a successful "no page found" case
        if (response.status === 404) {
          return null; // Page doesn't exist
        }

        if (error) {
          throw new Error((error as any)?.error || "Failed to fetch page");
        }

        return data as unknown as PageResponse;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled,
  });
}

export function useCreatePage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      entityType,
      entityUid,
      title,
      content,
      text_content,
    }: {
      entityType: string;
      entityUid: string;
      title?: string;
      content: TiptapDocument;
      text_content?: string;
    }): Promise<PageResponse> => {
      const requestBody: CreatePageRequest = {
        title,
        content,
        text_content,
      };

      // Use asset-specific API for assets, generic API for other entity types
      if (entityType === "asset") {
        const { data, error } = await apiClient.POST(
          "/api/v1/organizations/{orgCode}/assets/{assetUid}/page",
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                assetUid: entityUid,
              },
            },
            body: requestBody,
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to create asset page");
        }

        return data as unknown as PageResponse;
      } else {
        const { data, error } = await apiClient.POST(
          "/api/v1/organizations/{orgCode}/pages/{entityIdentifier}" as any,
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                entityIdentifier: `${entityType}:${entityUid}`,
              },
            },
            body: requestBody,
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to create page");
        }

        return data as unknown as PageResponse;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the page query
      queryClient.invalidateQueries({
        queryKey: ["pages", variables.entityType, variables.entityUid],
      });
      
      // For assets, also invalidate the asset cache since pages affect asset data
      if (variables.entityType === "asset") {
        queryClient.invalidateQueries({
          predicate: query => {
            const [method, path] = query.queryKey
            return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
          }
        });
      }
    },
  });
}

export function useUpdatePage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      entityType,
      entityUid,
      title,
      content,
      text_content,
    }: {
      entityType: string;
      entityUid: string;
      title?: string;
      content: TiptapDocument;
      text_content?: string;
    }): Promise<PageResponse> => {
      const requestBody: UpdatePageRequest = {
        title,
        content,
        text_content,
      };

      // Use asset-specific API for assets, generic API for other entity types
      if (entityType === "asset") {
        // For assets, both create and update use the same POST endpoint
        const { data, error } = await apiClient.POST(
          "/api/v1/organizations/{orgCode}/assets/{assetUid}/page",
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                assetUid: entityUid,
              },
            },
            body: requestBody,
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to update asset page");
        }

        return data as unknown as PageResponse;
      } else {
        const { data, error } = await apiClient.PATCH(
          "/api/v1/organizations/{orgCode}/pages/{entityIdentifier}" as any,
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                entityIdentifier: `${entityType}:${entityUid}`,
              },
            },
            body: requestBody,
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to update page");
        }

        return data as unknown as PageResponse;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the page query
      queryClient.invalidateQueries({
        queryKey: ["pages", variables.entityType, variables.entityUid],
      });
      
      // For assets, also invalidate the asset cache since pages affect asset data
      if (variables.entityType === "asset") {
        queryClient.invalidateQueries({
          predicate: query => {
            const [method, path] = query.queryKey
            return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
          }
        });
      }
    },
  });
}

export function useDeletePage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      entityType,
      entityUid,
    }: {
      entityType: string;
      entityUid: string;
    }): Promise<{ success: boolean }> => {
      // Use asset-specific API for assets, generic API for other entity types
      if (entityType === "asset") {
        const { data, error } = await apiClient.DELETE(
          "/api/v1/organizations/{orgCode}/assets/{assetUid}/page",
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                assetUid: entityUid,
              },
            },
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to delete asset page");
        }

        return data as { success: boolean };
      } else {
        const { data, error } = await apiClient.DELETE(
          "/api/v1/organizations/{orgCode}/pages/{entityIdentifier}" as any,
          {
            params: {
              path: {
                orgCode: "demo", // TODO: Get from context
                entityIdentifier: `${entityType}:${entityUid}`,
              },
            },
          }
        );

        if (error) {
          throw new Error((error as any)?.error || "Failed to delete page");
        }

        return data as { success: boolean };
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the page query
      queryClient.invalidateQueries({
        queryKey: ["pages", variables.entityType, variables.entityUid],
      });
      
      // For assets, also invalidate the asset cache since pages affect asset data
      if (variables.entityType === "asset") {
        queryClient.invalidateQueries({
          predicate: query => {
            const [method, path] = query.queryKey
            return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
          }
        });
      }
    },
  });
}